// Giriş ekranı mesajları (5 Eki 2026). Backend her yanıtta 8426xxx kodu,
// başlık (message), açıklama (errors) ve takip için ref döner — kod sözlüğü:
// demcas docs/login-errors.md. Bu dosya API katmanının ürettiği farklı hata
// biçimlerini (axios hatası, düz metin, {message, errors}) tek biçime çevirir.

export type AuthTone = "error" | "warning" | "info" | "success";

export type AuthMessage = {
  tone: AuthTone;
  title: string;
  message?: string;
  code?: number;
  ref?: string;
  http?: number;
  retryAfter?: number;
  /** 423 kilit yanıtı: SecurityLockPanel'e verilir */
  lock?: any;
  /** 416: kod adımı açılmalı */
  needsCode?: boolean;
  /** 416 + approver=owner: kod mağaza sahibine gitti */
  ownerCode?: boolean;
};

// Panelde üretilen kodlar (sunucuya ulaşılamadığında ya da kodsuz yanıtta).
export const CLIENT_CODES = {
  network: 8426901,
  unexpected: 8426902,
  rateLimited: 8426903,
} as const;

type Translate = (key: string, values?: Record<string, any>) => string;

const isCode = (v: unknown): v is number => typeof v === "number" && v >= 8426000 && v < 8427000;

/**
 * `t`: "auth" çeviri ad alanı (clientErrors.* anahtarları için).
 */
export function parseAuthError(err: any, t: Translate): AuthMessage {
  const data = err?.data ?? err?.response?.data ?? (typeof err === "object" ? err : undefined);
  const http: number | undefined = err?.code ?? err?.response?.status ?? err?.status;
  const code = isCode(data?.error_code) ? data.error_code : undefined;
  const ref: string | undefined = data?.ref ?? err?.requestId ?? undefined;
  const retryAfter = Number(data?.retry_after ?? 0) || undefined;

  // 423 kilit: panel ayrı gösterilir
  if (http === 423 && data?.locked) {
    return {
      tone: "error",
      title: data?.title ?? data?.message ?? t("clientErrors.locked"),
      message: data?.message,
      code,
      ref,
      http,
      retryAfter,
      lock: data,
    };
  }

  if (code) {
    const tone: AuthTone = http === 416 ? "info" : http === 429 ? "warning" : "error";
    return {
      tone,
      title: typeof data?.message === "string" ? data.message : t("clientErrors.unexpectedTitle"),
      message: typeof data?.errors === "string" ? data.errors : undefined,
      code,
      ref,
      http,
      retryAfter,
      needsCode: http === 416,
      ownerCode: data?.approver === "owner",
    };
  }

  // Sunucuya hiç ulaşılamadı
  if (!http && !data?.message) {
    return { tone: "error", title: t("clientErrors.networkTitle"), message: t("clientErrors.networkMessage"), code: CLIENT_CODES.network, ref };
  }

  if (http === 429) {
    // Eski uçlar kodsuz ama açıklamalı 429 dönüyor ("30 sn bekleyin" gibi): onu göster.
    const backendText = typeof data?.message === "string" && data.message.trim() !== "" ? data.message : undefined;
    return {
      tone: "warning",
      title: t("clientErrors.rateLimitedTitle"),
      message: backendText ?? t("clientErrors.rateLimitedMessage"),
      code: CLIENT_CODES.rateLimited,
      ref,
      http,
      retryAfter,
    };
  }

  // Kodsuz ama okunur bir mesaj (eski uçlar): göster, ama genel 500 metnini değil
  const title = typeof data?.message === "string" ? data.message : undefined;
  const message = typeof data?.errors === "string" ? data.errors : typeof err === "string" ? err : undefined;
  if (http && http < 500 && (title || message)) {
    return { tone: http === 416 ? "info" : "error", title: title ?? message ?? "", message: title ? message : undefined, ref, http, needsCode: http === 416 };
  }

  return {
    tone: "error",
    title: t("clientErrors.unexpectedTitle"),
    message: t("clientErrors.unexpectedMessage"),
    code: CLIENT_CODES.unexpected,
    ref,
    http,
  };
}

/** Panelin kendi ürettiği bilgi/uyarı mesajları için kısayol. */
export const authNotice = (tone: AuthTone, title: string, message?: string): AuthMessage => ({ tone, title, message });
